// Full offline prefetch: on login (online) pull ALL projects, plans, positions,
// defects, tickets, measures AND photo blobs into the offline caches so the whole
// app stays usable without a connection. JSON goes through `api` (IndexedDB cache +
// overlay); image/plan blobs go through a raw client so only the Service Worker
// caches them (keeps IndexedDB small). Progress is published for a UI bar.
import axios from "axios";
import api, { API, saveBin, hasBin } from "@/lib/api";
import { pullDelta } from "@/lib/deltaSync";
import { idb } from "@/lib/offlineDb";

const raw = axios.create({ baseURL: API, withCredentials: true });
const MARK = "__prefetch:blobs"; // persisted set of already-downloaded blob keys

let state = { running: false, done: 0, total: 0, pct: 0, finished: false, error: false };
const listeners = new Set();

export function getPrefetchState() { return state; }
export function subscribePrefetch(cb) {
  listeners.add(cb);
  try { cb(state); } catch (_) {}
  return () => listeners.delete(cb);
}
function emit(patch) {
  state = { ...state, ...patch };
  if (state.total > 0) state.pct = Math.min(100, Math.round((state.done / state.total) * 100));
  listeners.forEach((l) => { try { l(state); } catch (_) {} });
  try { window.dispatchEvent(new CustomEvent("prefetch-state", { detail: state })); } catch (_) {}
}

let started = false;

const warmJson = (url) => api.get(url).then(() => true).catch(() => false);
const warmBlob = (url) => raw.get(url, { responseType: "blob" }).then(() => true).catch(() => false);
// Plan file: Service-Worker cache + iOS-safe copy in IndexedDB (survives SW cache purges).
const warmPlanFile = (id) => raw.get(`/plans/${id}/file`, { responseType: "blob" }).then((r) => saveBin(`/plans/${id}/file`, r.data)).catch(() => false);
const getList = (url) => api.get(url).then((r) => (Array.isArray(r.data) ? r.data : [])).catch(() => []);
const tryList = (url) => api.get(url).then((r) => (Array.isArray(r.data) ? r.data : [])).catch(() => null);
export const READY_KEY = "__offline:projects"; // { projectId: timestamp } fully available offline
export const STATS_KEY = "__offline:stats"; // { projectId: { defects, tickets, photos } } stored on this device

// Count what is really in the local cache per project (deduped by id; photos only if the image was downloaded)
// plus the server totals from the last online lists (`total`), for the "12 von 14" comparison.
async function computeStats(done) {
  const stats = {};
  const seen = new Set();
  const add = (pid, k, id, tot = false) => {
    if (!pid || seen.has(`${tot}${k}${id}`)) return;
    seen.add(`${tot}${k}${id}`);
    const s = (stats[pid] = stats[pid] || { defects: 0, tickets: 0, photos: 0, total: { defects: 0, tickets: 0, photos: 0 }, missing: [] });
    (tot ? s.total : s)[k] += 1;
  };
  for (const e of await idb.all("cache")) {
    const key = e?.key || "";
    const d = e?.data;
    const sm = key.match(/^g:\/search\?kind=(defects|tickets)/);
    if (sm && d && Array.isArray(d[sm[1]])) {
      d[sm[1]].forEach((x) => x && (sm[1] === "defects" || x.position_id) && add(x.project_id, sm[1], x.id, true));
      continue;
    }
    if (!Array.isArray(d)) continue;
    const mm = key.match(/^\/positions\/[^/]+\/(defects|tickets)$/);
    if (mm) { d.forEach((x) => x && !x.is_deleted && add(x.project_id, mm[1], x.id)); continue; }
    if (key.startsWith("g:/media?")) d.forEach((x) => { if (!x) return; add(x.project_id, "photos", x.id, true); if (done[fileKey(x)]) add(x.project_id, "photos", x.id); });
  }
  // Photos known on the server but not downloaded to this device (for "Fehlende Fotos").
  const miss = new Set();
  for (const e of await idb.all("cache")) {
    if (!(e?.key || "").startsWith("g:/media?") || !Array.isArray(e.data)) continue;
    for (const x of e.data) {
      if (!x || !x.project_id || done[fileKey(x)] || miss.has(x.id) || !stats[x.project_id]) continue;
      miss.add(x.id);
      stats[x.project_id].missing.push({ id: x.id, project_id: x.project_id, name: x.name || "", description: x.description || "", created_at: x.created_at || "" });
    }
  }
  await idb.put("cache", { key: STATS_KEY, data: stats, ts: Date.now() });
}

// Bounded-concurrency runner so we don't open hundreds of parallel requests.
async function pool(items, worker, concurrency) {
  let i = 0;
  const run = async () => {
    while (i < items.length) { const idx = i++; await worker(items[idx]); }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) || 0 }, run));
}

const CORE = [
  "/dashboard", "/inspections/dashboard", "/inspections?scope=all",
  "/inspections/assets", "/inspections/categories", "/inspections/projects",
  "/inspections/records", "/inspections/measures?scope=open", "/inspections/settings",
  "/search?kind=defects", "/search?kind=tickets",
  "/overview/daily-reports", "/overview/documents", "/overview/quantities",
  "/tasks/summary", "/tasks/prefs", "/tasks/nav-prefs", "/tasks/assignees", "/tasks/team-summary",
  "/notifications", "/notifications/unread-count", "/defects/deletion-requests", "/defects/deletion-requests/count",
  "/employees", "/companies", "/categories", "/roles", "/inspections/reports", "/auth/my-accounts",
];

// Task lists + every task detail (Aufgaben / Dashboard open them offline).
async function warmTasks() {
  const list = async (scope) => (await api.get("/tasks", { params: { scope, page_size: 200 } }).catch(() => null))?.data?.items || [];
  const items = [...(await list("team")), ...(await list("mine"))]; // "mine" last = cached default list
  const seen = new Set();
  await pool(items.filter((t) => !seen.has(t.type + t.id) && seen.add(t.type + t.id)),
    (t) => warmJson(`/tasks/${t.type}/${t.id}`), 4);
}

const planKey = (p) => `plan:${p.id}:${p.updated_at || p.created_at || ""}`;
const fileKey = (f) => `file:${f.id}`; // files are write-once (immutable by id)

// Project detail page: project, logo, buildings -> floors -> plans, project defects,
// plus the project tabs (Tagesberichte, Dokumente incl. Dateien, Mengen, Positionen, Berichtsoptionen).
async function warmProject(pr) {
  let ok = await warmJson(`/projects/${pr.id}`);
  ok = (await warmJson(`/search?kind=defects&project_id=${pr.id}`)) && ok;
  await warmBlob(`/projects/${pr.id}/logo`);
  for (const t of ["quantities", "positions", "report-options"]) await warmJson(`/projects/${pr.id}/${t}`);
  const reports = await getList(`/projects/${pr.id}/daily-reports`);
  await pool(reports, (r) => warmJson(`/daily-reports/${r.id}`), 3);
  const docs = await getList(`/projects/${pr.id}/documents`);
  await pool(docs, (d) => warmBlob(`/files/${d.id}/download`), 3);
  const blds = await tryList(`/projects/${pr.id}/buildings`);
  if (!blds) ok = false;
  for (const b of blds || []) {
    const fls = await tryList(`/buildings/${b.id}/floors`);
    if (!fls) ok = false;
    for (const f of fls || []) ok = (await warmJson(`/floors/${f.id}/plans`)) && ok;
  }
  return ok;
}

async function warmPlanData(p) {
  await pullDelta(p.id);
  const a = await warmJson(`/plans/${p.id}`);
  const positions = await tryList(`/plans/${p.id}/positions`);
  await pool(positions || [], async (pos) => {
    const notes = await getList(`/positions/${pos.id}/notes`);
    for (const n of notes) {
      const ph = await getList(`/notes/${n.id}/photos`);
      for (const f of ph) await warmBlob(`/files/${f.id}/download?thumb=1`);
    }
  }, 4);
  return a && !!positions;
}

const warmPhoto = async (f) => (await warmBlob(`/files/${f.id}/download?thumb=1`)) && (await warmBlob(`/files/${f.id}/download`));

// Download the given (missing) photos to this device; returns the number that succeeded.
export async function prefetchPhotos(files) {
  if (!navigator.onLine) return 0;
  let done = {};
  try { const m = await idb.get("cache", MARK); done = (m && m.data) || {}; } catch (_) {}
  let n = 0;
  await pool(files, async (f) => { if (await warmPhoto(f)) { done[fileKey(f)] = 1; n += 1; } }, 4);
  try { await idb.put("cache", { key: MARK, data: done, ts: Date.now() }); await computeStats(done); } catch (_) {}
  // Projects that were incomplete ONLY because of photos become "Offline verfügbar" once none is missing.
  try {
    const po = (await idb.get("cache", PHOTO_ONLY_KEY))?.data || {};
    const st = (await idb.get("cache", STATS_KEY))?.data || {};
    const fixed = Object.keys(po).filter((pid) => !(st[pid]?.missing || []).length);
    if (fixed.length) await setReady(fixed, () => true);
  } catch (_) {}
  try { window.dispatchEvent(new Event("offline-ready-changed")); } catch (_) {}
  return n;
}

const PHOTO_ONLY_KEY = "__offline:photo_only"; // { projectId: 1 } incomplete only because photos are missing

async function setReady(ids, ok, photoOnly = () => false) {
  const r = await idb.get("cache", READY_KEY).catch(() => null);
  const data = { ...((r && r.data) || {}) };
  const p = await idb.get("cache", PHOTO_ONLY_KEY).catch(() => null);
  const po = { ...((p && p.data) || {}) };
  for (const id of ids) {
    if (ok(id)) data[id] = Date.now(); else delete data[id];
    if (!ok(id) && photoOnly(id)) po[id] = 1; else delete po[id];
  }
  await idb.put("cache", { key: READY_KEY, data, ts: Date.now() });
  await idb.put("cache", { key: PHOTO_ONLY_KEY, data: po, ts: Date.now() });
  try { const m = await idb.get("cache", MARK); await computeStats((m && m.data) || {}); } catch (_) {}
  try { window.dispatchEvent(new Event("offline-ready-changed")); } catch (_) {}
}

// Re-download ONE project completely (structure, plans, plan images, photos). Returns true if complete.
export async function prefetchProject(projectId) {
  if (!navigator.onLine) return false;
  let done = {};
  try { const m = await idb.get("cache", MARK); done = (m && m.data) || {}; } catch (_) {}
  let ok = await warmProject({ id: projectId });
  let okPhotos = true;
  const plans = (await getList("/overview/plans")).filter((p) => p.project_id === projectId);
  const media = await getList(`/media?category=photo,defect_photo&project_id=${projectId}`);
  await pool(plans, async (p) => {
    if (!(await warmPlanData(p))) ok = false;
    if (await warmPlanFile(p.id)) done[planKey(p)] = 1; else ok = false;
  }, 3);
  await pool(media, async (f) => { if (await warmPhoto(f)) done[fileKey(f)] = 1; else okPhotos = false; }, 4);
  try { await idb.put("cache", { key: MARK, data: done, ts: Date.now() }); } catch (_) {}
  await setReady([projectId], () => ok && okPhotos, () => ok);
  return ok && okPhotos;
}

// Re-download ONE plan (plan data, positions, plan image, its photos). Returns true if complete.
export async function prefetchPlan(planId) {
  if (!navigator.onLine) return false;
  let done = {};
  try { const m = await idb.get("cache", MARK); done = (m && m.data) || {}; } catch (_) {}
  let ok = await warmPlanData({ id: planId });
  const plan = (await api.get(`/plans/${planId}`).catch(() => null))?.data;
  if (await warmPlanFile(planId)) { if (plan) done[planKey(plan)] = 1; } else ok = false;
  const media = await tryList(`/media?category=photo,defect_photo&plan_id=${planId}`);
  if (!media) ok = false;
  await pool(media || [], async (f) => { if (await warmPhoto(f)) done[fileKey(f)] = 1; else ok = false; }, 4);
  try { await idb.put("cache", { key: MARK, data: done, ts: Date.now() }); } catch (_) {}
  return ok;
}

export async function prefetchAll(force = false) {
  if (!navigator.onLine || state.running) return;
  if (started && !force) return;
  started = true;
  emit({ running: true, finished: false, error: false, done: 0, total: 1, pct: 0 });

  // Persisted markers of blobs we already downloaded in a previous session.
  // After the first full download, later logins only pull new/changed data.
  let done = {};
  try { const m = await idb.get("cache", MARK); done = (m && m.data) || {}; } catch (_) {}

  try {
    const projects = await getList("/projects");
    const plans = await getList("/overview/plans");
    const media = await getList("/media?category=photo,defect_photo");

    const newPlans = [];
    for (const p of plans) if (!done[planKey(p)] || !(await hasBin(`/plans/${p.id}/file`))) newPlans.push(p);
    const inSw = async (f) => { try { return !!(window.caches && (await caches.match(`${API}/files/${f.id}/download`))); } catch (_) { return true; } };
    const newPhotos = [];
    for (const f of media) if (!done[fileKey(f)] || !(await inSw(f))) newPhotos.push(f); // re-download if the SW cache was purged

    // JSON lists + per-plan delta always run (cheap, incremental "current data"
    // refresh). Only missing blobs are (re)downloaded.
    const total = 2 + CORE.length + projects.length + plans.length + newPlans.length + newPhotos.length * 2;
    emit({ total, done: 2 });

    for (const u of CORE) { await warmJson(u); emit({ done: state.done + 1 }); }
    await warmTasks();

    const bad = new Set();
    const badPhotos = new Set();
    await pool(projects, async (pr) => {
      if (!(await warmProject(pr))) bad.add(pr.id);
      emit({ done: state.done + 1 });
    }, 3);

    // Every plan: refresh overlayable sub-data via incremental delta pull.
    await pool(plans, async (p) => {
      if (!(await warmPlanData(p))) bad.add(p.project_id);
      emit({ done: state.done + 1 });
    }, 3);

    // New/changed plan images only.
    await pool(newPlans, async (p) => {
      if (await warmPlanFile(p.id)) done[planKey(p)] = 1; else bad.add(p.project_id);
      emit({ done: state.done + 1 });
    }, 3);

    // New photos only (thumbnail + full image).
    await pool(newPhotos, async (f) => {
      const t = await warmBlob(`/files/${f.id}/download?thumb=1`);
      emit({ done: state.done + 1 });
      if (t && (await warmBlob(`/files/${f.id}/download`))) done[fileKey(f)] = 1; else if (f.project_id) badPhotos.add(f.project_id);
      emit({ done: state.done + 1 });
    }, 4);

    try { await idb.put("cache", { key: MARK, data: done, ts: Date.now() }); } catch (_) {}
    await setReady(projects.map((p) => p.id), (id) => !bad.has(id) && !badPhotos.has(id), (id) => !bad.has(id));

    emit({ done: state.total, running: false, finished: true });
  } catch (_) {
    emit({ running: false, error: true });
  } finally {
    try { await idb.put("cache", { key: MARK, data: done, ts: Date.now() }); } catch (_) {}
  }
}
